import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { pink } from '@mui/material/colors';
import {
    KeyboardDoubleArrowDown as KeyboardDoubleArrowDownIcon,
    KeyboardDoubleArrowUp as KeyboardDoubleArrowUpIcon,
    Add as AddIcon
} from "@mui/icons-material";

import {
    BasicLoader,
    FilledButton,
    GeneralChip,
    IconButton, Textfield, Typography,
    UploadImageList, useSnackbar
} from "@initia-digital/component-library"
import { baseDefinedAndNotNullNarrowing, S3 } from '@initia-digital/ts-utils';

import { SectionHeader } from "./SectionHeader"
import { useWorkAndSafetyStore } from "../store";
import { putS3Object } from '../api/objectStorage';
import { useMSALPayload } from '@initia-digital/auth-client-utils';
import { FileUploadStatus, IContact, ISiteContact, OtherPrestartMedia } from '@initia-digital/types/work-and-safety';
import { DEFAULT_APP_RESIZE_OPTIONS, DEFAULT_WEB_RESIZE_OPTIONS, loadFileUrl, scaleImage } from '../utils/files';
import { IPrestartStageProps } from '../interfaces/componentInterfaces';
import { ContractorsAutoComplete } from './Autosuggest';
import { usePlanDetail } from '../hooks';
import { SiteContact } from './SiteContact';



export const ClosureAndContacts = ({ handleSave }: IPrestartStageProps) => {
    // ------------------------------------
    const [areFilesLoading, setAreFilesLoading] = useState(false);

    const {
        editPrestartState, updateEditPrestartState
    } = useWorkAndSafetyStore();
    const {
        leadingTeam,
        contractorsAndSubcontractors,
        siteContacts,
        otherNotes
    } = editPrestartState;

    let prestartLabel = 'Take-5';
    let declarationLabel = 'reviewed our work and safety plan';
    if (leadingTeam) {
        prestartLabel = 'prestart meeting';
        declarationLabel = 'received this health and safety brief from an Initia staff member';
    }

    const params = useParams();
    const { jobId, planId } = params;

    const theme = useTheme();
    const isSmallerThanSm = useMediaQuery(theme.breakpoints.down('sm'));

    const { queueSnackbar } = useSnackbar();

    // AUTH AND DATA INTEGRATION
    // ---------------------
    const authDetail = useMSALPayload();

    // PLAN DETAIL
    const { planDetailData, planDetailLoading } = usePlanDetail(authDetail, planId!, baseDefinedAndNotNullNarrowing(planId));
    const { contractorsAndSubcontractors: planContractorsAndSubcontractors, contacts: planContacts } = planDetailData ?? { contractorsAndSubcontractors: [] as string[], contacts: { rows: [] as IContact[] } };

    const handleSiteContactUpdate = useCallback((
        siteContactUpdate: ISiteContact
    ) => {
        // ---------------------
        const siteContactListUpdate = siteContacts.rows.map(contact => {
            if (contact.id === siteContactUpdate.id) {
                return siteContactUpdate;
            }
            return contact;
        });
        updateEditPrestartState({
            ...editPrestartState,
            siteContacts: {
                rows: siteContactListUpdate
            }
        });
    }, [editPrestartState, siteContacts.rows]);

    const handleSiteContactDelete = useCallback((
        siteContact: ISiteContact
    ) => {
        // ---------------------
        const siteContactListUpdate = siteContacts.rows.filter(contact => contact.id !== siteContact.id);
        updateEditPrestartState({
            ...editPrestartState,
            siteContacts: {
                rows: siteContactListUpdate
            }
        });
    }, [editPrestartState, siteContacts.rows]);

    const loadFileUrlCb = useCallback((
        image: { filename: string } & OtherPrestartMedia
    ) => loadFileUrl(image, authDetail, queueSnackbar), [authDetail, queueSnackbar]);

    const handleOtherMediaUpload = useCallback(async (
        files: File[]
    ) => {
        // ---------------------
        const newOtherPrestartMediaList: OtherPrestartMedia[] = [];
        for (const file of files) {
            // -----------------------------------------
            setAreFilesLoading(true);
            try {
                // ------------------------------
                const fileId = uuidv4();
                const path = `${import.meta.env.VITE_ENV_LOWER}-work-and-safety/${jobId}/prestart/other-media/other-media-${fileId}/original/${S3.toSafePath(file.name)}`;
                const appPath = `${import.meta.env.VITE_ENV_LOWER}-work-and-safety/${jobId}/prestart/other-media/other-media-${fileId}/app/${S3.toSafePath(file.name)}`;
                const webPath = `${import.meta.env.VITE_ENV_LOWER}-work-and-safety/${jobId}/prestart/other-media/other-media-${fileId}/web/${S3.toSafePath(file.name)}`;

                const newOtherMedia = {
                    id: fileId,
                    jobId: (jobId as string),
                    planId: (planId as string),
                    prestartId: '',
                    path: path,
                    name: file.name,
                    notes: ``,
                    size: file.size,
                    fileType: file.type,
                    file,
                    uploadStatus: "PENDING" as FileUploadStatus,
                    includeInAppendix: false
                };

                // UPLOAD ORIGINAL TO S3
                await putS3Object(authDetail, {
                    file,
                    path
                });

                // Add file to data state
                newOtherPrestartMediaList.push(newOtherMedia);

                // HANDLE RESIZED IMAGE OPTIONS TOO
                const appFile = await scaleImage(file, DEFAULT_APP_RESIZE_OPTIONS);
                await putS3Object(authDetail, {
                    file: appFile,
                    path: appPath
                });
                const webFile = await scaleImage(file, DEFAULT_WEB_RESIZE_OPTIONS);
                await putS3Object(authDetail, {
                    file: webFile,
                    path: webPath
                });
            } catch (err) {
                // ------------------------------
                console.error(err);
                queueSnackbar(`There was an error uploading ${file.name}`, 'error', 'Error', 3000, '1rem');
            }
        }
        setAreFilesLoading(false);
        updateEditPrestartState({
            ...editPrestartState,
            otherPrestartMedia: [
                ...editPrestartState.otherPrestartMedia,
                ...newOtherPrestartMediaList
            ]
        });
        // Save the plan with file uploads
        // - This has been added initially to match with aniticipated user expectations
        await handleSave({
            ...editPrestartState,
            otherPrestartMedia: [...editPrestartState.otherPrestartMedia, ...newOtherPrestartMediaList]
        });

    }, [editPrestartState, handleSave, jobId, planId, queueSnackbar]);

    const handleOtherMediaDelete = useCallback(async (
        file: OtherPrestartMedia
    ) => {
        // ---------------------
        updateEditPrestartState({
            ...editPrestartState,
            otherPrestartMedia: editPrestartState.otherPrestartMedia.filter(media => media.id !== file.id)
        });
    }, [editPrestartState]);

    return (
        <Box
            id="closure-and-contacts"
            display="flex" flexDirection="column"
            border={isSmallerThanSm ? "none" : "1px solid lightgrey"}
            borderTop={`1px solid ${theme.palette.secondary.main}`} borderRadius={isSmallerThanSm ? "none" : "5px"}
            pb="20px" px={isSmallerThanSm ? '10px' : '20px'}
            mt={isSmallerThanSm ? '20px' : '0px'} pt={isSmallerThanSm ? '20px' : '0px'}
        >
            <Box display="flex" flexDirection="row" alignItems="center">
                <SectionHeader label="5. Closure and contacts" />
                <Box display="flex" flexDirection="row" justifyContent="flex-end">
                    <Box mr="10px">
                        <IconButton
                            size="vsmall"
                            onClick={() => window.location.hash = '#prestart-start'}
                            tooltipTitle={`Go to Start`}
                            color={theme.palette.secondary.contrastText}
                            backgroundColor={theme.palette.secondary.main}
                            hoverColor={theme.palette.primary.main}
                            hoverBackgroundColor={theme.palette.primary.contrastText}
                        >
                            <KeyboardDoubleArrowDownIcon />
                        </IconButton>
                    </Box>
                    <Box mr="10px">
                        <IconButton
                            size="vsmall"
                            onClick={() => window.location.hash = '#working-with-others'}
                            tooltipTitle={`Go to Working with Others`}
                            color={theme.palette.secondary.contrastText}
                            backgroundColor={theme.palette.secondary.main}
                            hoverColor={theme.palette.primary.main}
                            hoverBackgroundColor={theme.palette.primary.contrastText}
                        >
                            <KeyboardDoubleArrowUpIcon />
                        </IconButton>
                    </Box>
                </Box>
            </Box>
            <Box
                display="flex" flexDirection="row" gap={1} alignItems="center"
            >
                <Typography
                    fontSize="0.9rem"
                    fontWeight={700}
                    color={theme.palette.primary.contrastText}
                >
                    {`Other notes`}
                </Typography>
            </Box>
            <Box my="4px">
                <Textfield
                    value={otherNotes}
                    name="otherNotes"
                    minRows={3}
                    label="Other notes (e.g. what might be useful for future site visits)"
                    placeholder="Other notes"
                    handleChange={(event) => {
                        // ---------------------
                        updateEditPrestartState({
                            ...editPrestartState,
                            otherNotes: event.target.value
                        });
                    }}
                />
            </Box>
            <Box
                display="flex" flexDirection="row" gap={1} alignItems="center"
                mt="12px"
            >
                <Typography
                    fontSize="0.9rem"
                    fontWeight={700}
                    color={theme.palette.primary.contrastText}
                >
                    {`Other site photos`}
                </Typography>
            </Box>
            <Box>
                <Box>
                    <UploadImageList
                        images={editPrestartState.otherPrestartMedia.map(media => ({ ...media, filename: media.name }))}
                        loadImage={loadFileUrlCb}
                        handleImageUpload={handleOtherMediaUpload}
                        handleImageDelete={handleOtherMediaDelete}
                    />
                    {areFilesLoading && (
                        <BasicLoader
                            label="Loading"
                            size={80}
                        />
                    )}
                </Box>
            </Box>
            <Box
                display="flex" flexDirection="row" gap={1} alignItems="center"
                mt="16px"
            >
                <Typography
                    fontSize="0.9rem"
                    fontWeight={700}
                    color={theme.palette.primary.contrastText}
                >
                    {`Contractors (${contractorsAndSubcontractors.length})`}
                </Typography>
            </Box>
            {
                !planDetailLoading && baseDefinedAndNotNullNarrowing(planContractorsAndSubcontractors)
                && planContractorsAndSubcontractors.length > 0 &&
                (
                    <Box
                        display="flex"
                        flexDirection="row"
                        gap={1}
                        alignItems="center"
                        my="5px"
                    >
                        <FilledButton
                            size={isSmallerThanSm ? "vsmall" : "small"}
                            onClick={() => {
                                // ---------------------
                                const contractorsAndSubcontractorsUpdate = planContractorsAndSubcontractors;
                                updateEditPrestartState({
                                    ...editPrestartState,
                                    contractorsAndSubcontractors: contractorsAndSubcontractorsUpdate
                                });
                            }}
                        >
                            Use plan contractors
                        </FilledButton>
                        <Typography
                            fontSize="0.8rem"
                            fontWeight={300}
                            color={theme.palette.primary.contrastText}
                        >
                            {`or add using the search`}
                        </Typography>
                    </Box>
                )
            }
            <Box>
                <ContractorsAutoComplete
                    name="contractorsAndSubcontractors"
                    label="Which contractors and subcontractors are we working with?"
                    handleAutoSuggestSelectStateChange={(event) => {
                        // ---------------------
                        updateEditPrestartState({
                            ...editPrestartState,
                            [event.target.name]: event.target.value
                        });
                    }}
                    selectedValues={contractorsAndSubcontractors}
                />
            </Box>
            <Box>
                {
                    contractorsAndSubcontractors.length === 0 &&
                    <Box my="10px" mx="20px">
                        <Typography
                            fontSize={"0.8rem"}
                            fontWeight={300}
                            color={theme.palette.primary.contrastText}
                        >
                            No contractors or subcontractors added yet
                        </Typography>
                    </Box>
                }
                {
                    contractorsAndSubcontractors.length > 0 &&
                    <Box
                        display="flex" flexDirection="row" flexWrap="wrap"
                        rowGap="5px" columnGap="5px"
                        alignItems="center" justifyContent="flex-start"
                        mt="8px"
                    >
                        {
                            contractorsAndSubcontractors.map(contractor => {
                                return (
                                    <GeneralChip
                                        key={`contractorchip-${contractor}`}
                                        label={contractor}
                                        backgroundColor={theme.palette.secondary.main}
                                        size="small"
                                        handleDelete={() => {
                                            // ---------------------
                                            updateEditPrestartState({
                                                ...editPrestartState,
                                                contractorsAndSubcontractors: contractorsAndSubcontractors.filter(c => c !== contractor)
                                            });
                                        }}
                                    />
                                )
                            })
                        }
                    </Box>
                }
            </Box>
            <Box
                display="flex" flexDirection="row" gap={1} alignItems="center"
                mt="16px" mb="8px"
            >
                <Typography
                    fontSize="0.9rem"
                    fontWeight={700}
                    color={theme.palette.primary.contrastText}
                >
                    {`Contacts (${siteContacts.rows.length})`}
                </Typography>
            </Box>
            <Box
                border={`1px solid ${pink[500]}`}
                borderRadius="4px"
                p="10px"
            >
                <Box
                    display="flex" flexDirection="row" alignItems="center"
                >
                    <Typography
                        fontSize="0.9rem"
                        fontWeight={700}
                        color={theme.palette.primary.contrastText}
                    >
                        {`Individual declaration`}
                    </Typography>
                </Box>
                <Box
                    display="flex" flexDirection="row" alignItems="center"
                >
                    <Typography
                        fontSize="0.8rem"
                        fontWeight={300}
                        color={theme.palette.primary.contrastText}
                    >
                        {`By signing this ${prestartLabel} record you are confirming you have ${declarationLabel}`}
                    </Typography>
                </Box>
            </Box>
            <Box
                display="flex"
                flexDirection="row"
                gap={1}
                alignItems="center"
                my="5px"
            >
                {
                    !planDetailLoading && baseDefinedAndNotNullNarrowing(planContacts)
                    && planContacts.rows.length > 0 &&
                    (
                        <>
                            <FilledButton
                                size={isSmallerThanSm ? "vsmall" : "small"}
                                onClick={() => {
                                    // ---------------------
                                    const siteContactUpdate = planContacts.rows.map(contact => ({
                                        ...contact,
                                        contactId: contact.id,
                                        prestartId: '',
                                        contactSignatureClientData: [] as SignaturePad.Point[][]
                                    }) as ISiteContact);
                                    updateEditPrestartState({
                                        ...editPrestartState,
                                        siteContacts: {
                                            rows: siteContactUpdate
                                        }
                                    });
                                }}
                            >
                                Use plan contacts
                            </FilledButton>
                            <Typography
                                fontSize="0.8rem"
                                fontWeight={300}
                                color={theme.palette.primary.contrastText}
                            >
                                {`or`}
                            </Typography>
                        </>
                    )
                }
                <FilledButton
                    size={isSmallerThanSm ? "vsmall" : "small"}
                    startIcon={<AddIcon />}
                    onClick={() => {
                        // ---------------------
                        updateEditPrestartState({
                            ...editPrestartState,
                            siteContacts: {
                                rows: [
                                    ...editPrestartState.siteContacts.rows,
                                    {
                                        id: uuidv4(),
                                        jobId: jobId ?? '',
                                        planId: planId ?? '',
                                        prestartId: '',
                                        contactEmail: null,
                                        contactName: '',
                                        contactNumber: null,
                                        contactOrg: null,
                                        isOrgMain: false,
                                        contactSignatureClientData: [] as SignaturePad.Point[][]
                                    }
                                ]
                            }
                        });
                    }}
                >
                    Add
                </FilledButton>
            </Box>
            <Box
                display="flex"
                width="100%"
                sx={{
                    containerType: 'inline-size',
                    containerName: `site-contacts-container`,
                    width: '100%'
                }}
            >
                {
                    siteContacts.rows.length === 0 &&
                    <Box my="10px" mx="20px">
                        <Typography
                            fontSize={"0.8rem"}
                            fontWeight={300}
                            color={theme.palette.primary.contrastText}
                        >
                            No contacts added yet
                        </Typography>
                    </Box>
                }
                {
                    siteContacts.rows.length > 0 &&

                    <Box
                        display="flex"
                        width="100%"
                        sx={{
                            display: 'grid',
                            gap: '10px',
                            '@container site-contacts-container (min-width: 500px)': {
                                gridTemplateColumns: 'repeat(2, 1fr)'
                            },
                            '@container site-contacts-container (max-width: 499px)': {
                                gridTemplateColumns: isSmallerThanSm ? '1fr' : 'repeat(2, 1fr)'
                            }
                        }}
                    >
                        {siteContacts.rows.map(siteContact => {
                            return (
                                <SiteContact
                                    key={`sitecontact-${siteContact.id}`}
                                    siteContact={siteContact}
                                    handleSiteContactUpdate={handleSiteContactUpdate}
                                    handleSiteContactDelete={handleSiteContactDelete}
                                />
                            )
                        })}
                    </Box>
                }
            </Box>
        </Box>
    )
}